import RefreshIcon from "@assets/images/refresh-primary.svg";
import {
  Box,
  Button,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import InputSuggestion from "@saleor/alternatedMaterial/components/InputSuggestionForPrioridy";
import DatePicker from "@saleor/components/DatePicker";
import { FormSpacer } from "@saleor/components/FormSpacer";
import MyTypography from "@saleor/components/MyTypography";
import SingleSelectField from "@saleor/components/SingleSelectField";
import { SingleTextField } from "@saleor/components/SingleTextField";
import useAppState from "@saleor/hooks/useAppState";
import useOrderFilter from "@saleor/hooks/useOrderFilter";
import { mapEdgesToItems } from "@saleor/utils/maps";
import moment from "moment-timezone";
import React, { useEffect, useMemo } from "react";
import SVG from "react-inlinesvg";
import { FormattedMessage } from "react-intl";

import { OrderListFilterProps } from "../OrderListFilter";
import OrderStatus from "../OrderStatus";
import { DateSelectionType, FIELD_TYPE } from "./constant";
import useStyles from "./styles";

const OrderFilterSection: React.FC<OrderListFilterProps> = ({
  handleChangeFilter,
  message,
  listOrder,
  loading,
  setIsSearched
}) => {
  const classes = useStyles();
  const [{ filterOrder: filterAppState }] = useAppState();
  const {
    formik,
    setDataFormik,
    isOpen,
    setIsOpen,
    isClean,
    fetchSoldToDataSuggestions,
    loadingSoldToDataSuggestion,
    fetchShipToDataSuggestions,
    getSoldToDataSuggestions,
    getShipToDataSuggestions,
    loadingShipToDataSuggestion,
    handleCleanForm,
    formData,
    ArrowIconComponent,
    orderStatus
  } = useOrderFilter({ handleChangeFilter });

  useEffect(() => {
    if (!filterAppState) {
      handleCleanForm();
      return;
    }
    setDataFormik({
      ...filterAppState,
      gte: filterAppState.createDate
        ? filterAppState.createDate.gte
        : filterAppState.lastUpdate
        ? filterAppState.lastUpdate.gte
        : "",
      lte: filterAppState.createDate
        ? filterAppState.createDate.lte
        : filterAppState.lastUpdate
        ? filterAppState.lastUpdate.lte
        : "",
      dateSelectionType: filterAppState.createDate
        ? DateSelectionType.CREATE_DATE
        : DateSelectionType.LAST_UPDATE,
      statusSAP: filterAppState.statusSap?.length
        ? filterAppState.statusSap?.length === 1
          ? filterAppState.statusSap[0]
          : "ALL"
        : ""
    });
  }, [filterAppState]);

  const { piNo, eoNo, poNo, bu, company, soldTo, shipTo } = formik.values;

  useEffect(() => {
    if (!!((bu || company || soldTo || shipTo) && !piNo && !eoNo && !poNo)) {
      formik.setFieldValue("dateSelectionType", DateSelectionType.CREATE_DATE);
    }
  }, [piNo, eoNo, poNo, bu, company, soldTo, shipTo]);

  useEffect(() => {
    if (listOrder?.length) {
      setIsOpen(false);
    }
  }, [listOrder]);

  useEffect(() => {
    if (formik.values.lte) {
      const maxDiff = 90;
      const diff = moment(formik.values.lte).diff(
        moment(formik.values.gte),
        "days"
      );
      if (Math.abs(diff) > maxDiff) {
        const newLte = moment(formik.values.gte).add(maxDiff, "day");
        formik.setFieldValue("lte", newLte);
      }
    }
  }, [formik.values.gte]);

  useEffect(() => {
    if (formik.values.gte) {
      const maxDiff = 90;
      const diff = moment(formik.values.gte).diff(
        moment(formik.values.lte),
        "days"
      );
      if (Math.abs(diff) > maxDiff) {
        const newGte = moment(formik.values.lte).subtract(maxDiff, "day");
        formik.setFieldValue("gte", newGte);
      }
    }
  }, [formik.values.lte]);

  const getTextError = field => (
    <div className={classes.textError}>{formik.errors[field]}</div>
  );

  const isDisabled = useMemo(() => {
    if (
      !formik.values.piNo &&
      !formik.values.poNo &&
      !formik.values.eoNo &&
      !(formik.values.gte && formik.values.lte)
    ) {
      return true;
    }
    if (
      !formik.values.bu ||
      !formik.values.company ||
      !formik.values.sourceOfApp ||
      !formik.values.dateSelectionType
    ) {
      return true;
    }
    return !formik.dirty;
  }, [formik.values, formik.dirty]);

  const mappingDataSuggest = name => {
    if (name === "soldTo") {
      const filterSoldToExportOrder =
        fetchSoldToDataSuggestions?.suggestionSoldToChangeOrderSearch;
      return {
        totalCount: filterSoldToExportOrder?.totalCount,
        pageInfo: filterSoldToExportOrder?.pageInfo,
        showLoadMore: true,
        fetchDataSuggestions: fetchSoldToDataSuggestions,
        loadingDataSuggestion: loadingSoldToDataSuggestion,
        getDataSuggestions: getSoldToDataSuggestions,
        dataOptions:
          mapEdgesToItems(filterSoldToExportOrder)?.map(data => ({
            title: data.displayCodeName,
            value: data.partnerCode
          })) || []
      };
    } else {
      const filterShipToExportOrder =
        fetchShipToDataSuggestions?.suggestionShipToChangeOrderSearch;
      return {
        fetchDataSuggestions: fetchShipToDataSuggestions,
        loadingDataSuggestion: loadingShipToDataSuggestion,
        getDataSuggestions: getShipToDataSuggestions,
        totalCount: filterShipToExportOrder?.totalCount,
        pageInfo: filterShipToExportOrder?.pageInfo,
        showLoadMore: true,
        dataOptions:
          mapEdgesToItems(filterShipToExportOrder)?.map(data => ({
            title: data.displayCodeName,
            value: data.partnerCode
          })) || []
      };
    }
  };

  const renderItem = (
    <Grid className={classes.searchBox} container spacing={3}>
      {formData.map((child, key) => {
        const renderChild = (children: JSX.Element) => (
          <Grid item md={3} sm={6} key={key}>
            <MyTypography variant={"subtitle3"}>
              {child.title}
              {child.required ? (
                <span className={classes.required}>*</span>
              ) : null}
            </MyTypography>
            {children}
            {getTextError(child.name)}
          </Grid>
        );
        if (child.type === FIELD_TYPE.SINGLE_INPUT) {
          return renderChild(
            <SingleTextField
              name={child.name}
              id={child.name}
              InputProps={{
                inputProps: { "data-testid": `${child.name}-input` }
              }}
              value={formik.values[child.name]}
              onChange={formik.handleChange}
              placeholder={child.placeholder}
              error={!!formik.errors[child.name]}
              maxLengthProps={child?.maxLength ? child.maxLength : undefined}
            />
          );
        }
        if (child.type === FIELD_TYPE.SINGLE_SELECT) {
          return renderChild(
            <SingleSelectField
              className={classes.selectField}
              menuListCustomStyle={{
                overflowY: "auto"
              }}
              choices={child.options}
              name={child.name}
              id={child.name}
              testId={`${child.name}-select`}
              InputProps={{
                inputProps: { "data-testid": `${child.name}-input` }
              }}
              onChange={value => {
                formik.handleChange(value);
              }}
              value={formik.values[child.name] || child.defaultValue}
              placeholder={child.placeholder}
              error={!!formik.errors[child.name]}
            />
          );
        }
        if (child.type === FIELD_TYPE.NUMBER_INPUT) {
          return renderChild(
            <SingleSelectField
              className={classes.selectField}
              menuListCustomStyle={{
                overflowY: "auto"
              }}
              choices={child.options}
              name={child.name}
              id={child.name}
              InputProps={{
                inputProps: { "data-testid": `${child.name}-input` }
              }}
              onChange={value => {
                formik.handleChange(value);
              }}
              value={formik.values[child.name] || child.defaultValue}
              placeholder={child.placeholder}
              error={!!formik.errors[child.name]}
            />
          );
        }
        if (child.type === FIELD_TYPE.SUGGESTION_INPUT) {
          const dataSuggest = mappingDataSuggest(child.name);
          return renderChild(
            <InputSuggestion
              {...dataSuggest}
              contained
              key={`input${key}`}
              name={child.name}
              id={child.name}
              testId={child.name}
              disableDuplicationLoading
              withNonSelect
              searchField={"search"}
              defaultInputValue={formik.values[child.name]}
              defaultValue={{
                title: formik.values[child.name],
                value: formik.values[child.name]
              }}
              objectSuggestion
              placeholder={child.placeholder}
              error={!!formik.errors[child.name]}
              isClean={isClean}
              handleSelectedSearch={e => {
                formik.handleChange({
                  target: {
                    name: child.name,
                    value: typeof e === "string" ? e : e.value
                  }
                });
              }}
            />
          );
        }

        if (child.type === FIELD_TYPE.RADIO_GROUP) {
          return renderChild(
            <RadioGroup
              className={classes.radioGroup}
              aria-labelledby="demo-controlled-radio-buttons-group"
              name={child.name}
              id={child.name}
              data-testid={child.name}
              value={formik.values[child.name]}
              data-test-value={formik.values[child.name]}
              onChange={formik.handleChange}
            >
              {child.options.map((o, oKey) => (
                <FormControlLabel
                  key={`radio${oKey}`}
                  id={`radio${oKey}`}
                  data-testid={`${child.name}-option`}
                  value={o.value}
                  control={<Radio />}
                  label={
                    <MyTypography variant={"body3"}>{o.label}</MyTypography>
                  }
                />
              ))}
            </RadioGroup>
          );
        }
        if (child.type === FIELD_TYPE.DATEPICKER) {
          let maxDate = moment();
          let minDate;
          // this is start date
          if (child.name === "gte" && formik.values.lte) {
            minDate = undefined;
            maxDate = moment(formik.values.lte).isAfter(moment())
              ? moment()
              : moment(formik.values.lte);
          }
          // this is end date
          if (child.name === "lte" && formik.values.gte) {
            // const addMonth = moment(formik.values.gte).add(3, "month");
            maxDate = undefined;
            minDate = moment(formik.values.gte);
          }
          return renderChild(
            <DatePicker
              name={child.name}
              id={child.name}
              disableFuture
              disableManualInput
              value={formik.values[child.name] || ""}
              onChange={formik.handleChange}
              format="YYYY-MM-DD"
              isAllowTodayDate
              maxDate={maxDate ? maxDate.toDate() : undefined}
              minDate={minDate ? minDate.toDate() : undefined}
              error={!!formik.errors[child.name]}
            />
          );
        }
        if (child.type === FIELD_TYPE.MULTIPLE_SELECT) {
          return renderChild(
            <OrderStatus
              showChipDeleteButton={false}
              choices={orderStatus}
              name={child.name}
              id={child.name}
              value={formik.values[child.name]}
              onChange={formik.handleChange}
              isClean={isClean}
              placeholder={child.placeholder}
              numberItemDisplay={2}
              error={!!formik.errors[child.name]}
            />
          );
        }
      })}
    </Grid>
  );

  return (
    <>
      <Grid
        className={classes.header}
        container
        data-testid="orderFilterSection-collapseButton"
        onClick={() => setIsOpen(!isOpen)}
      >
        <MyTypography variant="h6">
          <FormattedMessage defaultMessage="ค้นหาคำสั่งซื้อ" />
        </MyTypography>
        <ArrowIconComponent className={classes.arrowIcon} />
      </Grid>
      <Collapse
        data-testid="orderFilterSection-collapseForm"
        data-test-in={isOpen}
        in={isOpen}
      >
        {renderItem}
        <Box className={classes.divider}>
          <FormSpacer />
          <Divider />
        </Box>
        <Box className={classes.bottomBar} ml={3}>
          {message}
          <Box className={classes.flex}>
            <Box mr={2}>
              <Button
                className={classes.resetButton}
                variant={"outlined"}
                color={"secondary"}
                disabled={!formik.dirty || loading}
                onClick={() => {
                  handleCleanForm();
                  setIsSearched(false);
                }}
                name="resetButton"
                id="resetButton"
                data-testid="resetButton"
              >
                <SVG
                  src={RefreshIcon}
                  className={classes.icon}
                  width={16}
                  height={16}
                />
                <FormattedMessage defaultMessage="ล้างข้อมูล" />
              </Button>
            </Box>
            <Box>
              <Button
                className={classes.actionButton}
                variant={"contained"}
                disabled={isDisabled || loading}
                color={"secondary"}
                onClick={formik.submitForm}
                name="submitButton"
                id="submitButton"
                data-testid="submitButton"
              >
                <SearchIcon style={{ fontSize: "18px" }} />
                <FormattedMessage defaultMessage="ค้นหา" />
              </Button>
            </Box>
          </Box>
        </Box>
      </Collapse>
    </>
  );
};

export default OrderFilterSection;
